export const MINIMUM_VOUCHER_LOAN_AMOUNT = 20000
export const HIGH_VOUCHER_LOAN_AMOUNT = 50000
export const STANDARD_VOUCHER_AMOUNT = 100
export const HIGH_VOUCHER_AMOUNT = 150

export const LEGACY_SMAVA_VOUCHER_LOAN_THRESHOLD = 30000

const isValidLoanAmount = (amount: unknown): amount is number =>
  typeof amount === 'number' && Number.isFinite(amount)

interface VoucherTier {
  threshold: number
  amount: number
}

// Shared by resolvers whose tiers are plain "amount >= threshold" lookups.
// Resolvers with non-standard boundaries (exclusive minimums, one-off
// exclusions) intentionally stay standalone below instead of bending this
// shape to fit them.
const resolveTieredAmount = (
  loanAmount: unknown,
  tiersDescending: VoucherTier[]
): number | null => {
  if (!isValidLoanAmount(loanAmount)) return null
  return (
    tiersDescending.find((tier) => loanAmount >= tier.threshold)?.amount ?? null
  )
}

export const resolveTieredVoucherAmount = (
  loanAmount: unknown
): number | null =>
  resolveTieredAmount(loanAmount, [
    { threshold: HIGH_VOUCHER_LOAN_AMOUNT, amount: HIGH_VOUCHER_AMOUNT },
    { threshold: MINIMUM_VOUCHER_LOAN_AMOUNT, amount: STANDARD_VOUCHER_AMOUNT }
  ])

export const resolveLegacySmavaVoucherAmount = (
  loanAmount: unknown
): number | null => {
  if (!isValidLoanAmount(loanAmount)) return null
  if (loanAmount >= HIGH_VOUCHER_LOAN_AMOUNT) return HIGH_VOUCHER_AMOUNT
  if (loanAmount > LEGACY_SMAVA_VOUCHER_LOAN_THRESHOLD) {
    return STANDARD_VOUCHER_AMOUNT
  }
  return null
}

/**
 * Resolves the standard FFG voucher amount:
 * - <50K  → no voucher
 * - >=50K → 150 €
 */
export const resolveFfgVoucherAmount = (loanAmount: unknown): number | null =>
  resolveTieredAmount(loanAmount, [
    { threshold: HIGH_VOUCHER_LOAN_AMOUNT, amount: HIGH_VOUCHER_AMOUNT }
  ])
